import { memo, useMemo, useState } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { platformFor } from '@deepdive-icons';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps } from '../../widgets/shared/cardShell';
import { SENTIMENT_BADGE, tableEmptyLabel, countFormat } from '../shared/tableShared';
import { TableWidgetActions, tableDraggingClass } from '../TableWidgetActions/TableWidgetActions';

/**
 * Competitors — `Competitive page performance` (2024:32704, in 2030:19055).
 *
 * Every brand page — one brand on one channel — with its engagement broken
 * down by type. A page is every mention of a brand on a channel; Reactions
 * carry likes, so Reactions + Comments + Shares = Total engagement.
 *
 * **Client-side sort and page**, as in the prototype: the contract returns the
 * full page list (≤ 35 rows) in fixture order, and this block sorts it.
 *
 * **Recut on 2026-09-23** (frame 931:36991). The file replaced the three volume
 * aggregates with `Sentiment`, `Intent` and `Topic`, renamed `Brand` to
 * `Company` and widened it to 370. Departures from the file, all settled
 * with the user:
 *
 *  - **`Total posts` stays.** A table whose rows carry no count at all cannot
 *    say what its interaction figures are built on, and the header's own
 *    "35 total pages" would be the only volume on screen.
 *  - **The ninth column is `Total engagement`, not `Engagement share`.** The
 *    file heads a column of counts `Engagement share` and hides a second
 *    column of percentages under the same name — the label travelled and the
 *    data did not.
 *  - **`Avg. engagement` and `Engagement share` are back** (2026-10-23), after
 *    `Total engagement`, as v2's table had them: engagement per post, and the
 *    page's percentage of every page's engagement.
 *
 * Three decisions the frame does not make:
 *
 *  1. **Columns sort** — every header but Channel and Page URL draws the sort
 *     chevron. Opens on Total engagement, highest first, because that is what
 *     "performance" ranks by.
 *  2. **Channel is a mark, not a button.** Figma wraps each logo in a Ghost Grey
 *     icon-only `Button`, but there is nothing for it to do.
 *  3. **No loading variant.** `Table` has none; see `tableEmptyLabel`.
 */

/** Figma draws ten body rows, which is also what its pagination implies. */
const PAGE_SIZE = 10;

/* An average of a few posts is often below 10, where a whole number would round the difference away. */
/** One page, labelled. */
export type PagePerformanceRowView = {
  /** `brand:channel`, stable across renders and filters. */
  id: string;
  brand: string;
  brandLabel: string;
  channel: string;
  channelLabel: string;
  /** The page's dominant values — the plurality of its mentions, not a property of the page. */
  sentiment: string;
  sentimentLabel: string;
  intentLabel: string;
  topicLabel: string;
  posts: number;
  engagement: number;
  /** Reactions **including likes**. */
  reactions: number;
  comments: number;
  shares: number;
  /** Total engagement per post; 0 for a page with no posts. */
  avgEngagement: number;
  /** This page's engagement as a percentage (0–100) of every page in the table, not only the ten on screen. */
  share: number;
};

export type CompetitivePagePerformanceView = {
  /** Every page, in fixture order (brand, then channel) — the tie order of every sort. */
  pages: PagePerformanceRowView[];
};

export type PagePerformanceSortKey =
  | 'brand'
  | 'sentiment'
  | 'intent'
  | 'topic'
  | 'posts'
  | 'engagement'
  | 'reactions'
  | 'comments'
  | 'shares'
  | 'avgEngagement'
  | 'share';
export type PagePerformanceSort = { key: PagePerformanceSortKey; direction: 'asc' | 'desc' };

export const DEFAULT_PAGE_PERFORMANCE_SORT: PagePerformanceSort = {
  key: 'engagement',
  direction: 'desc',
};

/** The three label columns sort A-Z; every figure opens highest first. */
const LABEL_KEYS = ['sentiment', 'intent', 'topic'] as const;
const opensAscending = (key: PagePerformanceSortKey) =>
  key === 'brand' || (LABEL_KEYS as readonly string[]).includes(key);

function compare(
  a: PagePerformanceRowView,
  b: PagePerformanceRowView,
  key: PagePerformanceSortKey,
): number {
  if (key === 'brand') {
    /* By name, then channel, so a brand's pages stay together. */
    return a.brandLabel.localeCompare(b.brandLabel) || a.channelLabel.localeCompare(b.channelLabel);
  }
  /*
   * The dominant-value columns sort by their **label**, not by fixture order:
   * the reader is looking at words. Ties fall back to the page.
   */
  if (key === 'sentiment')
    return a.sentimentLabel.localeCompare(b.sentimentLabel) || compare(a, b, 'brand');
  if (key === 'intent') return a.intentLabel.localeCompare(b.intentLabel) || compare(a, b, 'brand');
  if (key === 'topic') return a.topicLabel.localeCompare(b.topicLabel) || compare(a, b, 'brand');

  return a[key] - b[key];
}

/** The table's order for a sort — exported for the parity test. */
export function sortPagePerformance(
  pages: readonly PagePerformanceRowView[],
  sort: PagePerformanceSort,
): PagePerformanceRowView[] {
  const out = [...pages].sort((a, b) => compare(a, b, sort.key));
  return sort.direction === 'desc' ? out.reverse() : out;
}

/** Every column the table can draw: the sortable ones, the channel and `Page URL`. */
export type PagePerformanceColumnKey = PagePerformanceSortKey | 'channel' | 'url';

export type CompetitivePagePerformanceTableProps = WidgetBlockProps & {
  view?: CompetitivePagePerformanceView;
  /**
   * Figma `Page URL` → `View`. A no-op until there is somewhere to send it;
   * the hook is here so that becomes a change at the call site.
   */
  onViewPage?: (page: PagePerformanceRowView) => void;
  /**
   * Open the row — a click anywhere on it, or the Company text, which turns
   * into the row's link on hover (Figma 2024:32704). Competitors opens the Page
   * conversations modal (2125:84169). Omit and the rows stay inert, which is
   * what a custom report widget of this table wants.
   */
  onOpenPage?: (page: PagePerformanceRowView) => void;
  /**
   * The columns drawn, in the table's own order (the executive `Official
   * pages`: brand, platform, posts and engagement, GP 2026-11-03). Default
   * every column, at the measured widths. Fewer are sized for the table they
   * are in: the brand takes the width the rest leave, each of which hugs its
   * widest cell.
   */
  columns?: readonly PagePerformanceColumnKey[];
  /** Column headers where a page names them differently (`Brand`, `Platform`). */
  headers?: Partial<Record<PagePerformanceColumnKey, string>>;
  /** Default `Competitive page performance`. */
  title?: string;
  helpText?: string;
};

export const CompetitivePagePerformanceTable = memo(function CompetitivePagePerformanceTable({
  status,
  view,
  span = 12,
  chrome,
  onViewPage,
  onOpenPage,
  columns: drawn,
  headers,
  title = 'Competitive page performance',
  helpText = 'Compares each brand’s activity by channel, with post counts, common sentiment, intent and topic, and total engagement.',
}: CompetitivePagePerformanceTableProps) {
  const [sort, setSort] = useState<PagePerformanceSort>(DEFAULT_PAGE_PERFORMANCE_SORT);
  const [page, setPage] = useState(1);

  const pages = useMemo(() => (status === 'ready' && view ? view.pages : []), [status, view]);
  const sorted = useMemo(() => sortPagePerformance(pages, sort), [pages, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  /* Clamped on read rather than reset in an effect — Influencers' reason. */
  const currentPage = Math.min(page, pageCount);
  const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const sortFor = (key: PagePerformanceSortKey): TableHeaderSort =>
    sort.key === key ? sort.direction : 'selector';

  /*
   * A re-sort returns to page 1. Staying on page 4 of a new order shows ten
   * rows the reader has no way to place.
   */
  const onSort = (key: PagePerformanceSortKey) => () => {
    setPage(1);
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : /* A new column opens descending for every figure; words open A–Z. */
          { key, direction: opensAscending(key) ? 'asc' : 'desc' },
    );
  };

  /* Every column drawn, its measured width; fewer, the brand takes the rest and the others hug. */
  const size = (key: PagePerformanceColumnKey, width: string): Partial<TableColumn> =>
    !drawn ? { width } : key === 'brand' ? {} : { fit: true };
  const head = (key: PagePerformanceColumnKey, header: string) => headers?.[key] ?? header;

  const sortable = (
    key: PagePerformanceSortKey,
    header: string,
    width: string,
    extra?: Partial<TableColumn>,
  ): TableColumn => ({
    key,
    header: head(key, header),
    ...size(key, width),
    sort: sortFor(key),
    onSort: onSort(key),
    ...extra,
  });

  /*
   * Widths in px, each the width its header needs unclipped, measured at
   * 1440 (sort chevron and padding included). `Intent` and `Topic` are the
   * exception: sized past their headers for their widest words
   * (`Recommendation`, `Network coverage`), as before.
   *
   * Thirteen columns need 1429, more than the 1192 the table gets at 1440.
   * Under `table-layout: fixed` a table wider than its columns hands the spare
   * width out across them, and one narrower scrolls inside `Table`'s body. So a
   * wide screen shows every column and a 1440 one scrolls about 240px, rather
   * than clipping every header the way shares of 100% did. `Total posts` was
   * already clipped at 7.6% before the two columns came back.
   *
   * No `helpText` on any sortable column (button-in-a-button); the
   * explanation lives in the table header's tooltip instead.
   */
  const allColumns: TableColumn[] = [
    sortable('brand', 'Company', '120px'),
    /* The marks carry no text, so the column head is the only thing naming them. */
    {
      key: 'channel',
      header: head('channel', 'Channel'),
      cellType: 'actionIcons',
      ...size('channel', '70px'),
    },
    sortable('sentiment', 'Sentiment', '98px', { cellType: 'badge' }),
    /* Names run longer than these columns (`Information Sharing`); hovering a cut one shows it. */
    sortable('intent', 'Intent', '123px', { reveal: true }),
    sortable('topic', 'Topic', '129px', { reveal: true }),
    sortable('posts', 'Total posts', '103px'),
    sortable('reactions', 'Reactions', '97px'),
    sortable('comments', 'Comments', '103px'),
    sortable('shares', 'Shares', '81px'),
    sortable('engagement', 'Total engagement', '142px'),
    sortable('avgEngagement', 'Avg. engagement', '139px'),
    sortable('share', 'Engagement share', '146px'),
    {
      key: 'url',
      header: head('url', 'Page URL'),
      cellType: 'actionButtons',
      ...size('url', '78px'),
    },
  ];
  const columns = drawn
    ? allColumns.filter((column) => drawn.includes(column.key as PagePerformanceColumnKey))
    : allColumns;

  const tableRows: TableRowData[] = visible.map((row) => {
    /* The key on mock data, the name on live (where the key is an id). */
    const platform = platformFor(row.channel) ?? platformFor(row.channelLabel);
    return {
      key: row.id,
      onActivate: onOpenPage ? () => onOpenPage(row) : undefined,
      activateLabel: onOpenPage
        ? `Open conversations for ${row.brandLabel} on ${row.channelLabel}`
        : undefined,
      cells: {
        brand: { text: row.brandLabel },
        channel: {
          content: platform ? (
            <PlatformIcon name={platform.name} label={row.channelLabel} />
          ) : (
            /* A channel with no drawn mark falls back to its name, as ChartLegend does. */
            row.channelLabel
          ),
        },
        sentiment: {
          content: (
            <Badge type={SENTIMENT_BADGE[row.sentiment] ?? 'gray'}>{row.sentimentLabel}</Badge>
          ),
        },
        intent: { text: row.intentLabel },
        topic: { text: row.topicLabel },
        posts: { text: countFormat.format(row.posts) },
        reactions: { text: countFormat.format(row.reactions) },
        comments: { text: countFormat.format(row.comments) },
        shares: { text: countFormat.format(row.shares) },
        engagement: { text: countFormat.format(row.engagement) },
        avgEngagement: { text: countFormat.format(row.avgEngagement) },
        share: { text: `${row.share.toFixed(1)}%` },
        url: {
          content: (
            <Button
              hierarchy="ghostColor"
              iconTrailing={<Icon name="arrowUpRight" />}
              onClick={() => onViewPage?.(row)}
              aria-label={`View ${row.brandLabel} on ${row.channelLabel}`}
            >
              View
            </Button>
          ),
        },
      },
    };
  });

  const count = pages.length;

  return (
    <Table
      className={[spanClass(span), tableDraggingClass(chrome)].filter(Boolean).join(' ')}
      label={title}
      columns={columns}
      rows={tableRows}
      emptyLabel={tableEmptyLabel(status, 'No pages in this period')}
      header={
        <TableHeader
          flash={chrome?.flash}
          title={title}
          badge={`${countFormat.format(count)} total ${count === 1 ? 'page' : 'pages'}`}
          helpText={helpText}
          actions={<TableWidgetActions chrome={chrome} title={title} />}
        />
      }
      pagination={
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          onPageChange={setPage}
          label="Page performance pages"
        />
      }
    />
  );
});
